import React, { useCallback, useEffect, useRef, useState } from 'react';
import ServerTestSection from '../components/HomePage/ServerTestSection';
import ServerInfoCard from '../components/Shared/ServerInfoCard'; // Import ServerInfoCard
import ServerHackingTools from '../components/HomePage/ServerHackingTools';
import BotConnectionTools from '../components/HomePage/BotConnectionTools';
import AdditionalTools from '../components/HomePage/AdditionalTools';
import RecentTestedServers from '../components/HomePage/RecentTestedServers';
import ProofSection from '../components/HomePage/ProofSection';
import UserAccount from '../components/HomePage/UserAccount';
import SubscriptionPlans from '../components/HomePage/SubscriptionPlans';
import ServiceStats from '../components/HomePage/ServiceStats';
import PageTransition from '../components/Shared/PageTransition';
import { useAuth } from '../contexts/AuthContext';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import useWebSocket from '../hooks/useWebSocket';
import { WS_HOME_PAGE_TOPICS, WS_HOME_PAGE_TYPES } from '../config/wsHomePageConfig';
import axios from 'axios';
import { config } from "../config/appConfig";

const HomePage = () => {
  const { isConnectedToServer, isConnecting, connectToServer } = useServerConnection();
  const { isAuthenticated } = useAuth(); // Get user info
  const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket(); // Use WS hook
  const toolsEnabled = isAuthenticated && isConnectedToServer && !isConnecting

  // --- State for Home Page Data ---
  const [recentServers, setRecentServers] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [recentError, setRecentError] = useState(null);
  const [proofsError, setProofsError] = useState(null); // Separate error for proofs
  const [homeDataError, setHomeDataError] = useState(null);
  const subscriptions = useRef({});
  const dataRequestedRef = useRef(false);
  const serverInfoRef = useRef(null);

  // --- WebSocket Handlers for Home Data ---
  const handleRecentServers = useCallback((message) => {
    if (message?.type === WS_HOME_PAGE_TYPES.TYPE_RECENT_SERVERS_LIST && message.data?.history) {
      console.log("Received Recent Servers:", message.data.history);
      setRecentServers(message.data.history);
      setRecentError(null);
    }
  }, []);

  const handleHomeDataError = useCallback((message) => {
    const errorMsg = message?.error || message?.message || "Failed to load home page data.";
    console.error("Home Page WS Error:", errorMsg);
    setRecentError(errorMsg); // Assume it applies to recent servers for now
  }, []);

  const handleServerConnect = useCallback((ip) => {
    if (!ip || isConnecting) return;

    serverInfoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
      connectToServer(ip);
    }, 100)
  }, [connectToServer, isConnecting]);

  // Fetch Proofs via API on mount
  useEffect(() => {
    const fetchProofs = async () => {
      setProofsError(null);
      console.log("Fetching proofs via API...");
      try {
        const backendUrl = config.api.public.proofs;
        const response = await axios.get(backendUrl);
        console.log("API Proofs Response:", response.data);
        setProofs(response.data || []); // Update proofs state
      } catch (error) {
        console.error("Failed to fetch proofs:", error);
        setProofsError("Could not load proof videos."); // Set proofs error state
        setProofs([]); // Clear proofs on error
      }
    };
    fetchProofs();
    // No dependencies needed if it only runs once on mount
  }, []); // Empty dependency array means run once

  // --- WebSocket Subscription & Data Request Effect ---
  useEffect(() => {
    if (!isWebSocketConnected || !isAuthenticated) {
      if (subscriptions.current.recentServers) subscriptions.current.recentServers();
      if (subscriptions.current.proofs) subscriptions.current.proofs();
      if (subscriptions.current.error) subscriptions.current.error();
      subscriptions.current = {};
      dataRequestedRef.current = false;
      setRecentError("Not autenficated");
      return;
    }

    // Subscribe
    if (!subscriptions.current.recentServers) {
      subscriptions.current.recentServers = subscribe(WS_HOME_PAGE_TOPICS.USER_QUEUE_BASE + WS_HOME_PAGE_TOPICS.USER_RECENT_SERVERS, handleRecentServers);
    }
    if (!subscriptions.current.error) {
      subscriptions.current.error = subscribe(WS_HOME_PAGE_TOPICS.USER_QUEUE_BASE + WS_HOME_PAGE_TOPICS.USER_ERROR, handleHomeDataError);
    }

    // Request data once
    if (!dataRequestedRef.current) {
      setHomeDataError(null);
      console.log("Requesting Home Page Data...");
      sendMessage(WS_HOME_PAGE_TOPICS.APP_ENDPOINT_BASE + WS_HOME_PAGE_TOPICS.APP_GET_DATA, {});
      dataRequestedRef.current = true;

      // Simulate loading finish if no data arrives after a timeout (remove in real app)
      /*const timer = setTimeout(() => {
           if (isHomeDataLoading) { // Check if still loading
               setIsHomeDataLoading(false);
               console.warn("Home data loading timed out (using defaults/empty).");
           }
      }, 5000); // 5 second timeout for mock
      return () => clearTimeout(timer); // Clear timeout on cleanup/success*/
    }

    // Cleanup
    return () => {
      if (subscriptions.current.recentServers) subscriptions.current.recentServers();
      if (subscriptions.current.proofs) subscriptions.current.proofs();
      if (subscriptions.current.error) subscriptions.current.error();
      subscriptions.current = {};
      dataRequestedRef.current = false; // Allow request again on reconnect/auth
    };
  }, [isWebSocketConnected, isAuthenticated, subscribe, sendMessage, handleRecentServers, handleHomeDataError]); // Added isHomeDataLoading dependency

  console.log("Err: ", recentError, "  ", !recentError);
  return (
    <PageTransition>
      <div className="p-6 lg:p-8">
        <ServerTestSection />

        <div ref={serverInfoRef}>
          <ServerInfoCard />
        </div>

        {/* Conditionally render tools based on authentication */}
        <div className={`transition-opacity duration-500 ${!toolsEnabled ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
          <ServerHackingTools />
          <BotConnectionTools />
          <AdditionalTools />
          {/* Other sections */}
        </div>
        {/* Sections visible regardless of auth/connection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <RecentTestedServers
            servers={recentServers}
            isLoading={recentServers.length < 1 && !recentError}
            error={recentError}
            onServerSelect={handleServerConnect}
          />
          <ProofSection
            proofs={proofs}
            isLoading={proofs.length < 1}
            error={proofsError}
          />
        </div>
        {/*<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <UserAccount />}
        <SubscriptionPlans />
     </div>*/}
        <ServiceStats />
      </div>
    </PageTransition>
  );
};

export default HomePage;